/**
 * SuggestionCardShared - Shared sub-components for suggestion cards
 *
 * Extracts duplicated UI from ToFinchSuggestionCard and ToFilevineSuggestionCard:
 * - ConfidenceBadge: Confidence score display with hover tooltip
 * - SuggestionCardActions: Status-based action buttons (accept/reject/edit/retry)
 */

'use client';

import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Pencil,
  RefreshCw,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { getConfidenceColors } from '../../utils/confidenceUtils';

// ============================================================================
// Types
// ============================================================================

interface ConfidenceBadgeProps {
  confidence: number;
  aiReasoning?: string | null;
}

interface SuggestionCardActionsProps {
  status: string;
  isCreate: boolean;
  isAutoPushReview?: boolean;
  autoPushReviewStatus?: string | null;
  hasEdits?: boolean;
  /** Destination system label, e.g. "Finch" or "Filevine" */
  destinationLabel: string;
  /** Accent color scheme for the accept button and processing state */
  accentColor: 'emerald' | 'blue';
  card: {
    handleAccept: () => void;
    handleApprove: () => void;
    handleModify: () => void;
    handleReject: () => void;
    isAccepting: boolean;
    isRejecting: boolean;
    isActionInProgress: boolean;
    setEditModalOpen: (open: boolean) => void;
  };
}

// ============================================================================
// Confidence Badge
// ============================================================================

export const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({
  confidence,
  aiReasoning,
}) => {
  const colors = getConfidenceColors(confidence);

  return (
    <div className="relative group/confidence shrink-0">
      <div
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-help',
          colors.bg,
          colors.border,
        )}
      >
        <CheckCircle2 className={cn('w-4 h-4', colors.text)} />
        <span className={cn('text-sm font-semibold', colors.text)}>
          {Math.round(confidence)}%
        </span>
      </div>
      {aiReasoning && (
        <div className="absolute right-0 top-full mt-2 z-50 w-80 p-3 bg-zinc-900 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover/confidence:opacity-100 group-hover/confidence:visible transition-all duration-200">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
            <p className="leading-relaxed">{aiReasoning}</p>
          </div>
          <div className="absolute -top-1.5 right-4 w-3 h-3 bg-zinc-900 rotate-45" />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Action Buttons
// ============================================================================

const ACCENT_CLASSES = {
  emerald: {
    accept: 'bg-emerald-600 text-white hover:bg-emerald-700',
    processing: 'text-emerald-600',
  },
  blue: {
    accept: 'bg-blue-600 text-white hover:bg-blue-700',
    processing: 'text-blue-600',
  },
} as const;

export const SuggestionCardActions: React.FC<SuggestionCardActionsProps> = ({
  status,
  isCreate,
  isAutoPushReview = false,
  autoPushReviewStatus,
  hasEdits = false,
  destinationLabel,
  accentColor,
  card,
}) => {
  const accent = ACCENT_CLASSES[accentColor];

  return (
    <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100">
      {/* Auto-push review: Show Edit + Approve/Modify controls */}
      {isAutoPushReview && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => card.setEditModalOpen(true)}
            className="text-zinc-600"
          >
            <Pencil className="w-4 h-4 mr-1.5" />
            Edit
          </Button>

          <div className="flex items-center gap-2">
            <Button
              onClick={card.handleApprove}
              disabled={card.isActionInProgress || hasEdits}
              size="sm"
              variant="outline"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            >
              {card.isAccepting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
              )}
              Approve
            </Button>

            <Button
              onClick={card.handleModify}
              disabled={card.isActionInProgress || !hasEdits}
              size="sm"
              className={cn(
                'transition-all flex items-center gap-1.5',
                accent.accept,
              )}
            >
              {card.isAccepting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Modify
            </Button>
          </div>
        </div>
      )}

      {/* PENDING: Show Edit, Reject, Accept buttons */}
      {!isAutoPushReview && status === 'PENDING' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => card.setEditModalOpen(true)}
              className="text-zinc-600"
            >
              <Pencil className="w-4 h-4 mr-1.5" />
              Edit
            </Button>
            <button
              type="button"
              onClick={card.handleReject}
              disabled={card.isActionInProgress}
              className="text-sm font-medium text-zinc-500 hover:text-rose-600 transition-colors disabled:opacity-50"
            >
              {card.isRejecting ? 'Rejecting...' : 'Reject'}
            </button>
          </div>
          <Button
            onClick={card.handleAccept}
            disabled={card.isActionInProgress}
            size="sm"
            className={cn(
              'transition-all flex items-center gap-1.5',
              accent.accept,
            )}
          >
            {card.isAccepting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {isCreate
              ? `Create in ${destinationLabel}`
              : `Update ${destinationLabel}`}
          </Button>
        </div>
      )}

      {/* ACCEPTED / EXECUTING: Show syncing state */}
      {!isAutoPushReview && (status === 'ACCEPTED' || status === 'EXECUTING') && (
        <div
          className={cn(
            'flex items-center justify-center gap-2',
            accent.processing,
          )}
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">
            {status === 'ACCEPTED'
              ? 'Preparing sync...'
              : `Syncing to ${destinationLabel}...`}
          </span>
        </div>
      )}

      {/* COMPLETED: Show success state */}
      {!isAutoPushReview && status === 'COMPLETED' && (
        <div className="flex items-center justify-center gap-2 text-emerald-600">
          <CheckCircle2 className="w-4 h-4" />
          <span className="text-sm font-medium">
            Successfully synced to {destinationLabel}
          </span>
          {autoPushReviewStatus === 'ACCEPTED' && (
            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              Auto-push accepted
            </span>
          )}
          {autoPushReviewStatus === 'MODIFIED' && (
            <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
              Auto-push modified
            </span>
          )}
        </div>
      )}

      {/* FAILED: Show error state with retry */}
      {!isAutoPushReview && status === 'FAILED' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-rose-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Sync failed</span>
          </div>
          <Button
            onClick={card.handleAccept}
            disabled={card.isActionInProgress}
            size="sm"
            variant="outline"
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Retry
          </Button>
        </div>
      )}
    </div>
  );
};
